// MARK://dhk8/admin-shell/components/Tabs
// Segmented tablist accesible (role=tablist/tab + aria-selected). El tab activo se
// marca con FILL tint + weight (NADA de edge-strip / border-bottom de color), según
// la regla de marca. Materializa el patrón visual de `.adm-tabs`. Navegable con
// flechas ←/→ (Home/End) entre tabs.
import { useRef } from 'react'

export interface TabItem {
  id: string
  label: string
  count?: number
}

export interface TabsProps {
  tabs: TabItem[]
  active: string
  onChange: (id: string) => void
  /** aria-label del tablist (default "Secciones"). */
  ariaLabel?: string
}

export default function Tabs({ tabs, active, onChange, ariaLabel = 'Secciones' }: TabsProps) {
  const refs = useRef<(HTMLButtonElement | null)[]>([])

  function onKeyDown(e: React.KeyboardEvent, index: number) {
    let next = index
    if (e.key === 'ArrowRight') next = (index + 1) % tabs.length
    else if (e.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = tabs.length - 1
    else return
    e.preventDefault()
    const tab = tabs[next]
    if (tab) {
      onChange(tab.id)
      refs.current[next]?.focus()
    }
  }

  return (
    <div className="nui-tabs" role="tablist" aria-label={ariaLabel}>
      {tabs.map((tab, i) => {
        const selected = tab.id === active
        return (
          <button
            key={tab.id}
            ref={(el) => { refs.current[i] = el }}
            type="button"
            role="tab"
            id={`nui-tab-${tab.id}`}
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            className="nui-tabs__tab"
            data-selected={selected}
            onClick={() => onChange(tab.id)}
            onKeyDown={(e) => onKeyDown(e, i)}
          >
            <span className="nui-tabs__label">{tab.label}</span>
            {tab.count !== undefined && (
              <span className="nui-tabs__count">{tab.count}</span>
            )}
          </button>
        )
      })}
      <style>{`
        .nui-tabs {
          display: inline-flex;
          align-items: center;
          gap: 2px;
          padding: 3px;
          border-radius: var(--pos-radius-md, 8px);
          background: var(--pos-elevated);
          border: 1px solid var(--pos-separator);
          max-width: 100%;
          overflow-x: auto;
          scrollbar-width: none;
        }
        .nui-tabs::-webkit-scrollbar { display: none; }
        .nui-tabs__tab {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          min-height: 34px;
          padding: 0 var(--pos-space-3, 12px);
          border: none;
          border-radius: var(--pos-radius-sm, 6px);
          background: transparent;
          color: var(--pos-fg-secondary);
          font-family: var(--font-display, var(--font-sans));
          font-size: var(--pos-text-sm, 13px);
          font-weight: var(--pos-weight-medium, 500);
          white-space: nowrap;
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;
          transition: background var(--pos-duration-fast, 120ms) var(--pos-ease-out, ease),
                      color var(--pos-duration-fast, 120ms) var(--pos-ease-out, ease);
        }
        .nui-tabs__tab:hover {
          background: var(--pos-tint-soft);
          color: var(--pos-fg);
        }
        .nui-tabs__tab[data-selected="true"] {
          background: var(--pos-tint-fill);
          color: var(--pos-tint-on);
          font-weight: var(--pos-weight-semibold, 600);
        }
        .nui-tabs__tab:focus-visible {
          outline: none;
          box-shadow: 0 0 0 2px var(--pos-tint);
        }
        .nui-tabs__count {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 18px;
          height: 18px;
          padding: 0 5px;
          border-radius: var(--pos-radius-full, 9999px);
          background: color-mix(in oklch, currentColor 16%, transparent);
          font-size: var(--pos-text-xxs, 11px);
          font-weight: var(--pos-weight-semibold, 600);
          font-variant-numeric: tabular-nums;
        }
      `}</style>
    </div>
  )
}
